import { useState, useRef } from 'react';
import { Upload, Loader2, Check, Trash2, FolderOpen } from 'lucide-react';
import { api, type AssetOwnerType, type AssetType } from '../lib/api';

interface BulkItem {
  id: string;
  file: File;
  preview: string;
  name: string;
  assetType: AssetType;
  group: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

interface BulkAssetUploaderProps {
  ownerType: AssetOwnerType;
  ownerId: string;
  existingGroups?: string[];
  onComplete?: () => void;
  className?: string;
}

const ASSET_TYPES: { value: AssetType; label: string }[] = [
  { value: 'logo', label: 'Logo' },
  { value: 'banner', label: 'Banner' },
  { value: 'avatar', label: 'Avatar' },
];

export function BulkAssetUploader({ ownerType, ownerId, existingGroups = [], onComplete, className = '' }: BulkAssetUploaderProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<BulkItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [bulkGroup, setBulkGroup] = useState('');

  const addFiles = (files: FileList | File[], folderGroup?: string) => {
    const arr = Array.from(files);
    const newItems: BulkItem[] = arr
      .filter(f => f.size <= 5 * 1024 * 1024 && f.type.startsWith('image/'))
      .map(f => {
        // Derive group from folder path if available
        const pathGroup = (f as any).webkitRelativePath
          ? (f as any).webkitRelativePath.split('/').slice(0, -1).join('/') || ''
          : '';
        return {
          id: crypto.randomUUID(),
          file: f,
          preview: URL.createObjectURL(f),
          name: f.name.replace(/\.[^.]+$/, ''),
          assetType: 'logo' as AssetType,
          group: folderGroup || pathGroup || bulkGroup,
          status: 'pending' as const,
        };
      });
    setItems(prev => [...prev, ...newItems]);
  };

  const updateItem = (id: string, updates: Partial<BulkItem>) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const removeItem = (id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter(i => i.id !== id);
    });
  };

  const applyGroupToAll = (group: string) => {
    setBulkGroup(group);
    setItems(prev => prev.map(item => item.status === 'pending' ? { ...item, group } : item));
  };

  const uploadAll = async () => {
    setUploading(true);
    for (const item of items) {
      if (item.status !== 'pending') continue;
      updateItem(item.id, { status: 'uploading' });
      try {
        const { uploadUrl, assetId, s3Key } = await api.getAssetUploadUrl({
          ownerType, ownerId, assetType: item.assetType, contentType: item.file.type, fileSize: item.file.size,
          group: item.group || undefined,
        });
        const res = await fetch(uploadUrl, { method: 'PUT', body: item.file, headers: { 'Content-Type': item.file.type } });
        if (!res.ok) throw new Error('S3 upload failed');
        await api.registerAsset({
          assetId, s3Key, ownerType, ownerId, assetType: item.assetType,
          contentType: item.file.type, sizeBytes: item.file.size, name: item.name,
          group: item.group || undefined,
        });
        updateItem(item.id, { status: 'done' });
      } catch (err) {
        updateItem(item.id, { status: 'error', error: (err as Error).message });
      }
    }
    setUploading(false);
    onComplete?.();
  };

  const pendingCount = items.filter(i => i.status === 'pending').length;
  const doneCount = items.filter(i => i.status === 'done').length;

  return (
    <div className={className}>
      {/* Group for this batch */}
      <div className="mb-3">
        <label className="mb-1 block font-display text-[10px] font-800 uppercase tracking-[0.18em] text-iron-500">
          Group for this batch (optional)
        </label>
        <input
          type="text"
          value={bulkGroup}
          onChange={e => applyGroupToAll(e.target.value)}
          placeholder="e.g. Clan Logos, Season 4 Banners"
          className="input w-full rounded-lg text-sm"
          list="bulk-groups"
        />
        <datalist id="bulk-groups">
          {existingGroups.map(g => <option key={g} value={g} />)}
        </datalist>
      </div>

      {/* Drop zone + folder picker */}
      <div className="flex gap-3">
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          className="flex-1 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-iron-600 bg-iron-950 p-6 transition-colors hover:border-forge-500/50 hover:bg-iron-900"
        >
          <Upload className="h-6 w-6 text-iron-500" />
          <p className="text-sm text-iron-400">Drop images or click to browse</p>
          <p className="text-xs text-iron-500">PNG, JPG, WebP up to 5MB each</p>
        </div>

        <div
          onClick={() => folderRef.current?.click()}
          className="flex w-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-iron-600 bg-iron-950 p-4 transition-colors hover:border-forge-500/50 hover:bg-iron-900"
        >
          <FolderOpen className="h-6 w-6 text-iron-500" />
          <p className="text-xs text-iron-400 text-center">Upload folder</p>
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={e => {
        if (e.target.files?.length) addFiles(e.target.files);
        e.target.value = '';
      }} />
      {/* @ts-expect-error webkitdirectory is non-standard but widely supported */}
      <input ref={folderRef} type="file" webkitdirectory="" multiple className="hidden" onChange={e => {
        if (e.target.files?.length) {
          // Each file gets its subfolder path as the group
          // e.g. "MyAssets/Logos/clan.png" → group "MyAssets/Logos"
          const files = Array.from(e.target.files)
            .filter(f => f.size <= 5 * 1024 * 1024 && f.type.startsWith('image/'));
          const newItems: BulkItem[] = files.map(f => {
            const relPath = (f as any).webkitRelativePath || '';
            const parts = relPath.split('/');
            const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
            return {
              id: crypto.randomUUID(),
              file: f,
              preview: URL.createObjectURL(f),
              name: f.name.replace(/\.[^.]+$/, ''),
              assetType: 'logo' as AssetType,
              group: folderPath || bulkGroup,
              status: 'pending' as const,
            };
          });
          setItems(prev => [...prev, ...newItems]);
        }
        e.target.value = '';
      }} />

      {/* Item list */}
      {items.length > 0 && (
        <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-2 rounded-lg border border-iron-700 bg-iron-950 p-2.5">
              <img src={item.preview} alt="" className="h-9 w-9 rounded object-cover shrink-0" />

              <input
                type="text"
                value={item.name}
                onChange={e => updateItem(item.id, { name: e.target.value })}
                disabled={item.status !== 'pending'}
                className="input flex-1 min-w-0 rounded-lg text-xs py-1 px-2 disabled:opacity-50"
                placeholder="Name"
              />

              <select
                value={item.assetType}
                onChange={e => updateItem(item.id, { assetType: e.target.value as AssetType })}
                disabled={item.status !== 'pending'}
                className="input rounded-lg text-xs py-1 px-1.5 w-20 disabled:opacity-50"
              >
                {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <input
                type="text"
                value={item.group}
                onChange={e => updateItem(item.id, { group: e.target.value })}
                disabled={item.status !== 'pending'}
                className="input rounded-lg text-xs py-1 px-2 w-28 disabled:opacity-50"
                placeholder="Group"
                list="bulk-groups"
              />

              <div className="w-7 flex justify-center shrink-0">
                {item.status === 'pending' && (
                  <button onClick={() => removeItem(item.id)} className="text-iron-500 hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                {item.status === 'uploading' && <Loader2 className="h-3.5 w-3.5 animate-spin text-forge-400" />}
                {item.status === 'done' && <Check className="h-3.5 w-3.5 text-green-400" />}
                {item.status === 'error' && (
                  <span title={item.error} className="text-red-400 text-xs font-bold">!</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {items.length > 0 && (
        <div className="flex items-center justify-between pt-3">
          <p className="text-xs text-iron-500">
            {doneCount}/{items.length} uploaded
            {pendingCount > 0 && ` · ${pendingCount} pending`}
          </p>
          <div className="flex gap-2">
            {!uploading && (
              <button
                onClick={() => { items.forEach(i => URL.revokeObjectURL(i.preview)); setItems([]); }}
                className="rounded-lg px-3 py-1.5 text-xs text-iron-500 hover:text-iron-300 transition-colors"
              >
                Clear All
              </button>
            )}
            {pendingCount > 0 && (
              <button
                onClick={uploadAll}
                disabled={uploading}
                className="ember rounded-lg px-4 py-1.5 font-display text-[10px] font-800 uppercase tracking-wider disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : `Upload ${pendingCount} Asset${pendingCount !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
